import React, { createContext, useContext, useMemo } from 'react'

import { useDrawerControlForDataGrid } from 'hooks/use-drawer-control-for-data-grid'

import type { Agent } from 'types/api/agents/agents'

export type AgentDetailsDrawerActiveItem = { id: string }

type AgentDetailsDrawerContextValues = ReturnType<
  typeof useDrawerControlForDataGrid<AgentDetailsDrawerActiveItem>
> & {
  activeAgent: Agent | undefined
}

const AGENT_DETAILS_DRAWER_CONTEXT_DEFAULT_VALUES: AgentDetailsDrawerContextValues =
  {
    isOpen: false,
    activeItem: null,
    activeAgent: undefined,
    handleRowClick: () => {},
    handleClose: () => {},
    handleOpen: () => {}
  }

const AgentDetailsDrawerContext =
  createContext<AgentDetailsDrawerContextValues>(
    AGENT_DETAILS_DRAWER_CONTEXT_DEFAULT_VALUES
  )

export const useAgentDetailsDrawerContext = () => {
  const context = useContext(AgentDetailsDrawerContext)

  if (context === undefined) {
    throw new Error(
      'useAgentDetailsDrawerContext must be used within a AgentDetailsDrawerProvider'
    )
  }

  return context
}

export const AgentDetailsDrawerProvider: React.FC<{ agents: Agent[] }> = ({
  agents,
  children
}) => {
  const { isOpen, activeItem, handleRowClick, handleClose, handleOpen } =
    useDrawerControlForDataGrid<{ id: string }>()

  const activeAgent = useMemo(() => {
    if (activeItem) {
      return agents.find((agent) => agent.id === activeItem.id)
    }

    return
  }, [activeItem, agents])

  const value = useMemo(() => {
    return {
      isOpen,
      activeAgent,
      activeItem,
      handleRowClick,
      handleClose,
      handleOpen
    }
  }, [isOpen, activeAgent, activeItem, handleRowClick, handleClose, handleOpen])

  return (
    <AgentDetailsDrawerContext.Provider value={value}>
      {children}
    </AgentDetailsDrawerContext.Provider>
  )
}
